import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { TradingCostItem } from '../../proto/generated/order'

export const formatInr = (value: number) =>
    `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const STATUTORY_COST_TYPES = [
    'SEBI_TURNOVER_FEES',
    'EXCHANGE_TXN_CHARGES',
    'STAMP_DUTY',
    'STT',
    'GST',
] as const

const COST_LABELS: Record<string, string> = {
    SEBI_TURNOVER_FEES: 'SEBI turnover fee',
    EXCHANGE_TXN_CHARGES: 'Exchange turnover fee',
    STAMP_DUTY: 'Stamp duty',
    STT: 'Transaction tax (STT)',
    GST: 'GST',
    BROKERAGE: 'Brokerage',
}

const captionSx = {
    fontSize: 10,
    color: 'text.tertiary',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
} as const

type CostRowProps = {
    label: string
    amount: number
    emphasize?: boolean
}

const CostRow = ({ label, amount, emphasize = false }: CostRowProps) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography fontSize={14} color="text.secondary" fontWeight={emphasize ? 'medium' : 'regular'}>
            {label}
        </Typography>
        <Typography fontSize={14} color="text.primary" fontWeight={emphasize ? 'medium' : 'regular'}>
            {formatInr(amount)}
        </Typography>
    </Stack>
)

export type ChargesBreakdownPanelProps = {
    items: TradingCostItem[]
    total: number
    requiredMargin?: number
    availableMargin?: number
    brokerCode?: string
    getAmount: (costType: string) => number
}

const isPaperTradeBroker = (brokerCode?: string) =>
    brokerCode === 'PAPER_TRADE' || brokerCode === 'PAPER_TRADING'

const ChargesBreakdownPanel = ({
    total,
    requiredMargin,
    availableMargin,
    brokerCode,
    getAmount,
}: ChargesBreakdownPanelProps) => {
    const showAddFunds = availableMargin != null && !isPaperTradeBroker(brokerCode)

    return (
    <Stack spacing={0} sx={{ minWidth: 280 }}>
        <Box
            sx={{
                mx: -1.5,
                mt: -1.5,
                px: 1.5,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography fontSize={16} fontWeight={500} color="text.primary" lineHeight={1.4}>
                Charges & Taxes
            </Typography>
        </Box>
        <Stack spacing={1} sx={{ pt: 1.25, pb: 0.25 }}>
            <Typography variant="caption" sx={captionSx}>
                Government Taxes & Exchange Fees
            </Typography>
            {STATUTORY_COST_TYPES.map((costType) => (
                <CostRow key={costType} label={COST_LABELS[costType]} amount={getAmount(costType)} />
            ))}
            <Divider sx={{ borderColor: 'divider', my: 0.25 }} />
            <CostRow label={COST_LABELS.BROKERAGE} amount={getAmount('BROKERAGE')} />
            <Divider sx={{ borderColor: 'divider', my: 0.25 }} />
            <CostRow label="Total charges & taxes" amount={total} emphasize />
            {requiredMargin != null && <CostRow label="Required margin" amount={requiredMargin} />}
            {availableMargin != null && <CostRow label="Available margin" amount={availableMargin} />}
            {showAddFunds && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.25 }}>
                    <Button
                        size="small"
                        variant="text"
                        sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: 12, fontWeight: 500 }}
                    >
                        Add funds
                    </Button>
                </Box>
            )}
        </Stack>
    </Stack>
    )
}

export default ChargesBreakdownPanel
